import { useRef } from 'react';

import { notify } from '@cast/design-system';
import { parseApiError } from '@cast/api-client';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import { RffFormRef } from '@cast/shared-rff-ui';
import {
  useEvictorConfigQuery,
  useEvictorAdvancedConfigQuery,
} from '@cast/shared-workload-eviction-data-access';
import { useAutoscalerFeaturesQuery } from '@cast/split-me/hooks/queries/autoscaler';
import { useWoopInstallationStatus } from '@cast/split-me/features/workload-optimization/hooks';
import {
  EvictorConfig,
  EvictorAdvancedConfig,
} from '@cast/types-workload-eviction';
import { makeRefetchOnlyFailed, notReachable } from '@cast/utils';

import { EvictorSettingsForm } from './_components/EvictorSettingsForm';
import { FormValues } from './_types';
import { useSaveEvictorSettings } from './_hooks/useSaveEvictorSettings';

type LoadableData =
  | { type: 'loading' }
  | { type: 'error'; error: unknown; refetch: () => void }
  | {
      type: 'success';
      config: EvictorConfig;
      advancedConfig: EvictorAdvancedConfig;
      isWoopInstalled: boolean;
      clusterAgentVersion: string | undefined;
    };

const useLoadableData = (): LoadableData => {
  const { cluster } = useCluster();
  const {
    data: config,
    isLoading: isConfigLoading,
    error: configError,
    refetch: configRefetch,
  } = useEvictorConfigQuery({
    params: { clusterId: cluster.id },
  });
  const {
    data: advancedConfig,
    isLoading: isAdvancedConfigLoading,
    error: advancedConfigError,
    refetch: advancedConfigRefetch,
  } = useEvictorAdvancedConfigQuery({
    params: { clusterId: cluster.id },
  });
  const {
    features,
    isLoading: isFeaturesLoading,
    error: featuresError,
    refetch: featuresRefetch,
  } = useAutoscalerFeaturesQuery(cluster.id);
  const {
    isInstalled: isWoopInstalled,
    isLoading: isWoopLoading,
    error: woopError,
    refetch: woopRefetch,
  } = useWoopInstallationStatus();

  if (
    isConfigLoading ||
    isAdvancedConfigLoading ||
    isFeaturesLoading ||
    isWoopLoading
  ) {
    return { type: 'loading' };
  }

  const { error, refetch } = makeRefetchOnlyFailed(
    [configError, configRefetch],
    [advancedConfigError, advancedConfigRefetch],
    [featuresError, featuresRefetch],
    [woopError, woopRefetch]
  );

  if (error) {
    return {
      type: 'error',
      error,
      refetch,
    };
  }

  if (!config || !advancedConfig) {
    return {
      type: 'error',
      error: new Error(
        `Evictor ${
          advancedConfig ? 'config' : 'advanced config'
        } not found in API response`
      ),
      refetch,
    };
  }

  return {
    type: 'success',
    config,
    advancedConfig: advancedConfig || { evictionConfig: [] },
    isWoopInstalled,
    clusterAgentVersion: features?.agentVersion,
  };
};

export const EvictorSettings = () => {
  const loadableData = useLoadableData();

  const formRef = useRef<RffFormRef<FormValues>>(null);
  const saveEvictorSettings = useSaveEvictorSettings();

  switch (loadableData.type) {
    case 'loading':
      return <EvictorSettingsForm.Skeleton />;
    case 'error':
      return <EvictorSettingsForm.Error refetch={loadableData.refetch} />;
    case 'success':
      return (
        <EvictorSettingsForm
          ref={formRef}
          evictorConfig={loadableData.config}
          advancedConfig={loadableData.advancedConfig}
          clusterAgentVersion={loadableData.clusterAgentVersion}
          isWoopInstalled={loadableData.isWoopInstalled}
          isPending={saveEvictorSettings.isSaving}
          onSubmit={({ config, advancedConfig }) => {
            saveEvictorSettings.submit(
              {
                initialConfig: loadableData.config,
                initialAdvancedConfig: loadableData.advancedConfig,
                config,
                advancedConfig,
              },
              {
                onSuccess: () => {
                  notify.success('Bin-packing settings updated');
                  formRef.current?.form.restart();
                },
                onError: (error) => {
                  notify.error(parseApiError(error));
                },
              }
            );
          }}
        />
      );
    default:
      return notReachable(loadableData);
  }
};
